import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import axios from "axios";
import { 
  Heart, 
  Users, 
  Building, 
  FileText, 
  Shield, 
  Search,
  Clock,
  Star,
  Scale,
  Home,
  Briefcase,
  Gavel,
  TrendingUp
} from "lucide-react";

const Services = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/api/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      setError('Failed to load services. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Extract unique categories from services
  const categories = ["All", ...new Set(services.map(service => service.category))];

  const filteredServices = services.filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || service.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getIconForCategory = (category) => {
    switch (category) {
      case "Family Law": return Heart;
      case "Business & Corporate": return Building;
      case "Wills, Estates & Probate": return FileText;
      case "Criminal Record / Expungement": return Shield;
      case "Real Estate & Property Law": return Home;
      case "Patent Applications & IP Protection": return Shield;
      case "Trademark Registration & Brand Protection": return Scale;
      case "Investment & Startup Legal": return TrendingUp;
      case "Bankruptcy & Debt Relief": return Gavel;
      case "Document Review & Legal Support": return FileText;
      default: return FileText;
    }
  };

  const handleStartService = async (service) => {
    // Check if user is logged in and is a test account
    const authToken = localStorage.getItem('authToken');
    const userEmail = localStorage.getItem('user_email');
    
    // Test accounts bypass checkout and go directly to questionnaire
    if (authToken && (userEmail === 'formforgepro@gmail.com' || userEmail === 'forgeformpro@gmail.com')) {
      // Test account - skip checkout, go directly to questionnaire
      navigate(`/questionnaire/${service.id}`);
    } else {
      // Regular users go to checkout
      navigate(`/checkout/${service.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()} className="bg-teal-500 hover:bg-teal-600 text-white">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Legal Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Professional legal documents prepared by AI, customized for your state and situation.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto mb-8">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search legal services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 text-lg border-gray-300 rounded-lg"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? 
                    "bg-teal-500 hover:bg-teal-600 text-white" : 
                    "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredServices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No services found</h3>
              <p className="text-gray-600">Try adjusting your search or category filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => {
                const IconComponent = getIconForCategory(service.category);
                return (
                  <Card key={service.id} className="hover:shadow-lg transition-shadow border-gray-200">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="p-2 bg-teal-100 rounded-lg">
                            <IconComponent className="w-5 h-5 text-teal-600" />
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {service.category}
                          </Badge>
                        </div>
                        {service.popular && (
                          <Badge className="bg-teal-500 text-white">
                            <Star className="w-3 h-3 mr-1" />
                            Popular
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="text-lg font-semibold text-gray-800">
                        {service.title}
                      </CardTitle>
                    </CardHeader>
                    
                    <CardContent>
                      <p className="text-gray-600 mb-4 text-sm">
                        {service.description}
                      </p>
                      
                      <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {service.duration}
                        </div>
                        <div className="flex items-center">
                          ${service.price}
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="font-medium text-gray-800 mb-2">Included:</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {service.features.slice(0, 3).map((feature, index) => (
                            <li key={index} className="flex items-center">
                              <span className="w-1 h-1 bg-teal-500 rounded-full mr-2"></span>
                              {feature}
                            </li>
                          ))}
                          {service.features.length > 3 && (
                            <li className="text-gray-500 text-xs">
                              +{service.features.length - 3} more items
                            </li>
                          )}
                        </ul>
                      </div>

                      <div className="space-y-3">
                        <Button 
                          onClick={() => handleStartService(service)}
                          className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold"
                        >
                          Start Now - ${service.price}
                        </Button>
                        
                        {/* Add-on Services */}
                        <div className="text-center text-xs text-gray-500">
                          Add-ons available:
                        </div>
                        <div className="flex flex-wrap gap-1 text-xs">
                          <Badge variant="outline" className="text-teal-600 border-teal-300">
                            Document Review ($149)
                          </Badge>
                          <Badge variant="outline" className="text-teal-600 border-teal-300">
                            Filing Support ($99)
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Choose your legal service and let our AI guide you through the process
          </p>
          <Button 
            size="lg"
            className="bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-3"
            onClick={() => window.scrollTo(0, 0)}
          >
            Browse All Services
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;